import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Cake, Search, ShieldCheck, PartyPopper, User as UserIcon, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function Avatar({ member, size = 80 }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!member.has_photo) { setBlobUrl(null); return; }
    let url;
    let cancelled = false;
    api.get(`/users/${member.id}/photo`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => setBlobUrl(null));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [member.id, member.has_photo]);

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={member.full_name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover ring-4 ring-white shadow-md"
      />
    );
  }
  const colors = [
    "bg-[#0066FF]/15 text-[#0066FF]",
    "bg-violet-100 text-violet-700",
    "bg-emerald-100 text-emerald-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-sky-100 text-sky-700",
  ];
  const idx = (member.id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length;
  return (
    <div
      style={{ width: size, height: size }}
      className={`rounded-full flex items-center justify-center font-heading font-bold ring-4 ring-white shadow-md ${colors[idx]}`}
    >
      <span style={{ fontSize: size * 0.36 }}>{initials(member.full_name)}</span>
    </div>
  );
}

function BirthdayBadge({ member }) {
  if (!member.birthday_month || !member.birthday_day) return null;
  const label = `${member.birthday_day} ${MONTHS[member.birthday_month - 1]}`;
  const isToday = member.days_until_birthday === 0;
  if (isToday) {
    return (
      <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-gradient-to-r from-rose-100 to-amber-100 border border-rose-200 rounded-full px-3 py-1 animate-pulse">
        <span aria-hidden>🎂</span>
        Anniversaire aujourd'hui&nbsp;!
      </div>
    );
  }
  return (
    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
      <Cake className="h-3.5 w-3.5 text-rose-500" />
      {label}
    </div>
  );
}

function bdayInLabel(m) {
  if (m.days_until_birthday === 0) return "Aujourd'hui !";
  if (m.days_until_birthday === 1) return "Demain";
  return `Dans ${m.days_until_birthday} j`;
}

function ContactActions({ member, onOpenDocs }) {
  // Sanitize phone to a tel:/WhatsApp-friendly form (strip everything but +/digits).
  const raw = (member.phone || "").trim();
  const clean = raw.replace(/[^\d+]/g, "");
  const wa = clean.replace(/^\+/, "");
  if (!clean) return null;
  const stop = (e) => e.stopPropagation();
  const btn = "h-8 w-8 inline-flex items-center justify-center rounded-full border transition-colors";
  return (
    <div className="mt-2 flex justify-center gap-2" data-testid={`team-contact-${member.id}`} onClick={stop}>
      <a
        href={`tel:${clean}`}
        title={`Appeler ${member.full_name}`}
        aria-label={`Appeler ${member.full_name}`}
        data-testid={`team-call-${member.id}`}
        className={`${btn} border-slate-200 text-slate-700 hover:bg-[#0066FF] hover:text-white hover:border-[#0066FF]`}
      >
        <Phone className="h-3.5 w-3.5" />
      </a>
      <a
        href={`sms:${clean}`}
        title={`SMS à ${member.full_name}`}
        aria-label={`Envoyer un SMS à ${member.full_name}`}
        data-testid={`team-sms-${member.id}`}
        className={`${btn} border-slate-200 text-slate-700 hover:bg-violet-600 hover:text-white hover:border-violet-600`}
      >
        <MessageSquare className="h-3.5 w-3.5" />
      </a>
      <a
        href={`https://wa.me/${wa}`}
        target="_blank" rel="noreferrer"
        title={`WhatsApp — ${member.full_name}`}
        aria-label={`WhatsApp ${member.full_name}`}
        data-testid={`team-wa-${member.id}`}
        className={`${btn} border-slate-200 text-slate-700 hover:bg-emerald-500 hover:text-white hover:border-emerald-500`}
      >
        <WhatsAppGlyph />
      </a>
    </div>
  );
}

function WhatsAppGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .01 5.36.01 11.99c0 2.11.55 4.17 1.6 5.99L0 24l6.19-1.62a11.9 11.9 0 0 0 5.8 1.49h.01c6.63 0 11.99-5.36 11.99-11.99 0-3.2-1.25-6.21-3.47-8.4ZM12 21.3h-.01a9.29 9.29 0 0 1-4.74-1.3l-.34-.2-3.67.96.98-3.58-.22-.37A9.24 9.24 0 0 1 2.7 12c0-5.13 4.17-9.3 9.3-9.3 2.48 0 4.82.97 6.58 2.73a9.23 9.23 0 0 1 2.72 6.58c0 5.13-4.17 9.29-9.3 9.29Zm5.09-6.94c-.28-.14-1.65-.81-1.9-.9-.26-.09-.44-.14-.63.14-.19.28-.72.9-.88 1.08-.16.19-.32.21-.6.07a7.6 7.6 0 0 1-2.24-1.38 8.4 8.4 0 0 1-1.55-1.93c-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49l-.54-.01c-.18 0-.49.07-.75.35-.26.28-.98.96-.98 2.35 0 1.38 1 2.71 1.15 2.9.14.19 1.98 3.02 4.79 4.24.67.29 1.19.47 1.6.6.67.22 1.28.19 1.76.12.54-.08 1.65-.67 1.88-1.32.23-.65.23-1.21.16-1.33-.07-.13-.25-.19-.53-.34Z"/>
    </svg>
  );
}

function TeamCard({ member, currentUserId }) {
  const isMe = member.id === currentUserId;
  const isToday = member.days_until_birthday === 0;
  const nav = useNavigate();
  const openDocs = () => {
    // RH can view anyone's docs; employees can only view their own via /employe/documents-pro
    if (isMe) nav("/employe/documents-pro");
    else nav(`/rh/documents-pro/${member.id}`);
  };
  return (
    <Card
      data-testid={`team-card-${member.id}`}
      onClick={openDocs}
      className={`relative border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all pt-10 pb-5 px-4 text-center overflow-visible cursor-pointer ${isToday ? "ring-2 ring-rose-200 bg-gradient-to-b from-rose-50/60 to-white" : ""}`}
    >
      {isMe && (
        <span className="absolute top-2 right-2 text-[10px] uppercase font-semibold tracking-wider text-[#0066FF] bg-[#0066FF]/10 rounded-full px-2 py-0.5">Vous</span>
      )}
      {member.role === "rh" && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
          <ShieldCheck className="h-3 w-3" /> RH
        </span>
      )}
      {isToday && (
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-rose-500 to-amber-500 rounded-full px-2.5 py-1 shadow-md">
          🎂 Anniversaire
        </span>
      )}
      <div className="flex justify-center -mt-4 mb-3">
        <Avatar member={member} size={80} />
      </div>
      <div className="font-heading font-semibold text-slate-900 leading-tight">{member.full_name}</div>
      <div className="text-xs text-slate-500 mt-1 min-h-[16px]">{member.poste || "—"}</div>
      <ContactActions member={member} />
      <div className="flex justify-center"><BirthdayBadge member={member} /></div>
    </Card>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/team"); setMembers(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upcoming = useMemo(() => members.filter((m) => m.birthday_this_week), [members]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter((m) => (m.full_name || "").toLowerCase().includes(s) || (m.poste || "").toLowerCase().includes(s));
  }, [members, q]);

  return (
    <div>
      <PageHeader
        title="L'équipe FIO TELECOM"
        subtitle="Retrouvez ici toute l'équipe : photo, poste et anniversaire."
      />

      {upcoming.length > 0 && (
        <Card className="p-5 mb-6 border-0 bg-gradient-to-r from-rose-50 via-amber-50 to-white" data-testid="birthdays-week">
          <div className="flex items-center gap-2 mb-4">
            <PartyPopper className="h-5 w-5 text-rose-500" />
            <h2 className="font-heading font-semibold text-slate-900">Anniversaires de la semaine</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {upcoming.map((m) => (
              <div key={m.id} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 pl-2 pr-4 py-2 shadow-sm">
                <Avatar member={m} size={40} />
                <div>
                  <div className="text-sm font-medium text-slate-900 leading-tight">{m.full_name}</div>
                  <div className="text-xs text-slate-500">
                    {m.birthday_day} {MONTHS[m.birthday_month - 1]} · <span className="text-rose-600 font-medium">{bdayInLabel(m)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mb-5 relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="team-search"
          placeholder="Rechercher un collègue ou un poste…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500 border-slate-200">Chargement…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <UserIcon className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Aucun collègue ne correspond à votre recherche</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {filtered.map((m) => (
            <TeamCard key={m.id} member={m} currentUserId={user?.id} />
          ))}
        </div>
      )}
    </div>
  );
}
